import type { IProperty } from 'src/services/api';

export type ISimilarityFilters = {
    radiusMiles: number;
    /** Match this property type exactly; null means any type. */
    propertyType: IProperty['type'];
    rentMin: number | null;
    rentMax: number | null;
    bedroomsMin: number | null;
    bedroomsMax: number | null;
    bathroomsMin: number | null;
    isVacantOnly: boolean;
};

export type ISimilarProperty = {
    property: IProperty;
    /**
     * Distance from the anchor, or null when there is no geocoded anchor to measure
     * from and the results are therefore not distance-ranked.
     */
    distanceMiles: number | null;
};

export const RADIUS_OPTIONS = [1, 3, 5, 10, 25, 50] as const;

const EARTH_RADIUS_MILES = 3958.8;
const DEG_TO_RAD = Math.PI / 180;

/**
 * Great-circle distance. Exact enough at neighbourhood scale and cheap enough to run
 * over every property in a team on each filter change.
 */
export const distanceInMiles = (
    latA: number,
    lngA: number,
    latB: number,
    lngB: number
): number => {
    const dLat = (latB - latA) * DEG_TO_RAD;
    const dLng = (lngB - lngA) * DEG_TO_RAD;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(latA * DEG_TO_RAD) * Math.cos(latB * DEG_TO_RAD) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
};

const RENT_TOLERANCE = 0.2;
const BEDROOM_TOLERANCE = 1;
const DEFAULT_RADIUS_MILES = 5;

export type ILatLng = { latitude: number; longitude: number };

/**
 * The point to measure distances from, or null when there is nothing to measure from:
 * either the lead has no assigned property, or that property has never been geocoded.
 */
export const getAnchorPosition = (anchor: IProperty | null): ILatLng | null => (
    anchor != null && anchor.latitude != null && anchor.longitude != null ?
        { latitude: anchor.latitude, longitude: anchor.longitude } :
        null
);

/**
 * Seed the filters from the lead's own property so the modal opens on a useful answer
 * rather than an empty form: comparable rent, comparable size, nearby, and available.
 *
 * With no assigned property there is nothing to be comparable to, so only the vacancy
 * filter is applied and the view becomes "every vacant property the team has".
 */
export const getDefaultFilters = (anchor: IProperty | null): ISimilarityFilters => ({
    radiusMiles: DEFAULT_RADIUS_MILES,
    /*
     * A lead assigned to a house is house-hunting: same-type matches are the useful
     * default, and the toolbar makes the constraint visible and removable.
     */
    propertyType: anchor?.type ?? null,
    rentMin: anchor?.rent != null ?
        Math.floor((anchor.rent * (1 - RENT_TOLERANCE)) / 50) * 50 :
        null,
    rentMax: anchor?.rent != null ?
        Math.ceil((anchor.rent * (1 + RENT_TOLERANCE)) / 50) * 50 :
        null,
    bedroomsMin: anchor?.num_bedrooms != null ?
        Math.max(0, anchor.num_bedrooms - BEDROOM_TOLERANCE) :
        null,
    bedroomsMax: anchor?.num_bedrooms != null ?
        anchor.num_bedrooms + BEDROOM_TOLERANCE :
        null,
    bathroomsMin: null,
    isVacantOnly: true,
});

/**
 * Rank properties by how close they are to ``anchor``, keeping only those inside the
 * filters. The anchor itself is always excluded.
 *
 * Without a geocoded anchor the radius is not applied and results come back sorted by
 * name, so a lead with no assigned property still gets the team's vacant inventory
 * rather than an empty map.
 *
 * Properties that have never been geocoded are always excluded, so the result list is
 * exactly what the caller can draw on a map. Their count is reported separately rather
 * than being dropped silently.
 */
export const findSimilarProperties = (
    anchor: IProperty | null,
    properties: IProperty[],
    filters: ISimilarityFilters
): { matches: ISimilarProperty[]; skippedWithoutCoordinates: number } => {
    const anchorPosition = getAnchorPosition(anchor);

    const matches: ISimilarProperty[] = [];
    let skippedWithoutCoordinates = 0;

    for (const property of properties) {
        if (anchor != null && property.id === anchor.id) {
            continue;
        }
        if (property.latitude == null || property.longitude == null) {
            skippedWithoutCoordinates++;
            continue;
        }
        if (filters.isVacantOnly && !property.is_vacant) {
            continue;
        }
        if (filters.propertyType != null && property.type !== filters.propertyType) {
            continue;
        }
        // a property with unknown rent cannot be shown to satisfy a rent bound
        if (filters.rentMin != null && (property.rent == null || property.rent < filters.rentMin)) {
            continue;
        }
        if (filters.rentMax != null && (property.rent == null || property.rent > filters.rentMax)) {
            continue;
        }
        if (filters.bedroomsMin != null && (property.num_bedrooms ?? 0) < filters.bedroomsMin) {
            continue;
        }
        if (filters.bedroomsMax != null && (property.num_bedrooms ?? 0) > filters.bedroomsMax) {
            continue;
        }
        if (filters.bathroomsMin != null && (property.num_bathrooms ?? 0) < filters.bathroomsMin) {
            continue;
        }

        let distanceMiles: number | null = null;
        if (anchorPosition) {
            distanceMiles = distanceInMiles(
                anchorPosition.latitude,
                anchorPosition.longitude,
                property.latitude,
                property.longitude
            );
            if (distanceMiles > filters.radiusMiles) {
                continue;
            }
        }

        matches.push({ property, distanceMiles });
    }

    if (anchorPosition) {
        matches.sort((a, b) => (a.distanceMiles as number) - (b.distanceMiles as number));
    } else {
        matches.sort((a, b) => a.property.name.localeCompare(b.property.name));
    }

    return { matches, skippedWithoutCoordinates };
};

export const formatDistance = (miles: number): string => {
    if (miles < 0.1) {
        return 'same address';
    }
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
};
